import { findOptionsByValues } from "src/utils/dataListOptions";
import { isValidStandardNumber } from "src/utils/formatting";
import type { TemplateActionField } from "src/api/client-api/actionPlanTemplates/useTemplateActionOptions";
import type { TemplateAction, TemplateFieldValue } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";

export const templateFieldUnit = (field: TemplateActionField) => {
    if (field.contributionTypes.some((type) => type.toLowerCase().includes("percentage") || type === "%")) {
        return "%";
    }

    const unit =
        field.unit?.abbreviation ??
        field.actionFormulas.find((formula) => formula.targetSsqProperty?.unit?.abbreviation)?.targetSsqProperty?.unit
            ?.abbreviation;

    return unit && !["str", "int"].includes(unit) ? unit : undefined;
};

export const templateBoundsUnit = (field: TemplateActionField) => {
    const unit = field.actionFormulas.find((formula) => formula.targetSsqProperty?.unit?.abbreviation)
        ?.targetSsqProperty?.unit?.abbreviation;

    return unit && !["str", "int"].includes(unit) ? unit : undefined;
};

export const templateShareBases = (field: TemplateActionField) => {
    if (
        field.contributionTypes.includes("percentage") ||
        !field.contributionTypes.includes("absolute") ||
        templateFieldType(field) !== "number"
    ) {
        return [];
    }

    const bases = field.actionFormulas
        .map((formula) => formula.targetSsqProperty)
        .filter(
            (property): property is NonNullable<typeof property> & { id: number; title: string } =>
                property?.id !== undefined && property.title !== undefined,
        );

    return [...new Map(bases.map((property) => [property.id, property])).values()];
};

export const templateShareUnit = (field: TemplateActionField, baseSsqPropertyId: number | null | undefined) => {
    const unit = templateShareBases(field).find((property) => property.id === baseSsqPropertyId)?.unit?.abbreviation;
    return unit && !["str", "int"].includes(unit) ? unit : undefined;
};

export const templateAllowsBounds = (field: TemplateActionField, entry: TemplateFieldValue) =>
    entry.mode === "share" ||
    new Set(
        field.actionFormulas
            .map((formula) => formula.targetSsqProperty?.id)
            .filter((id): id is number => id !== undefined),
    ).size <= 1;

export const templateShareValueError = (entry: TemplateFieldValue) =>
    entry.mode === "share" &&
    (!isValidStandardNumber(entry.value) || Number(entry.value) <= 0 || Number(entry.value) > 100);

export const templateShareUnitMismatch = (field: TemplateActionField, entry: TemplateFieldValue) => {
    if (entry.mode !== "share") {
        return false;
    }

    const base = templateShareUnit(field, entry.baseSsqPropertyId);
    const own = templateFieldUnit(field);

    return base !== undefined && own !== undefined && base !== own;
};

export const templateBoundsError = (entry: TemplateFieldValue): "number" | "range" | null => {
    if (!entry.boundsEnabled) {
        return null;
    }

    const minimum = entry.minimumValue?.trim();
    const maximum = entry.maximumValue?.trim();
    if ((minimum && !isValidStandardNumber(minimum)) || (maximum && !isValidStandardNumber(maximum))) {
        return "number";
    }

    if (minimum && maximum && Number(minimum) > Number(maximum)) {
        return "range";
    }

    return null;
};

export const templateFieldType = (field: TemplateActionField): string => {
    if (field.field?.type) {
        return field.field.type;
    }

    return (
            field.contributionTypes.some((type) =>
                ["percentage", "absolute", "difference", "%"].includes(type.toLowerCase()),
            )
        ) ?
            "number"
        :   "text";
};

const restoreSavedFieldValue = (
    saved: TemplateFieldValue,
    field: TemplateActionField | undefined,
): TemplateFieldValue => {
    if (saved.dataListOptionCodes?.length || !field?.dataList || !saved.value) {
        return { ...saved };
    }

    const options = findOptionsByValues(field.dataList.options, saved.value.split("|"));

    return { ...saved, dataListOptionCodes: options.map((option) => option.code) };
};

const defaultFieldValue = (field: TemplateActionField): TemplateFieldValue => {
    const initial = { actionFieldId: field.id, value: field.defaultValue ?? "", position: 0, dataListOptionCodes: [] };

    if (!field.dataList || !field.defaultValue) {
        return initial;
    }

    const options = [];

    for (const id of field.defaultValue.split("|")) {
        const option = field.dataList.options.find((item) => String(item.id) === id);

        if (!option) {
            return { ...initial, value: "" };
        }

        options.push(option);
    }

    return {
        ...initial,
        value: options.map((option) => option.value).join("|"),
        dataListOptionCodes: options.map((option) => option.code),
    };
};

export const initialTemplateFieldValues = (fields: TemplateActionField[], saved: TemplateFieldValue[]) => {
    const seenFields = new Set<number>();
    const values = saved.flatMap((value) => {
        const field = fields.find((item) => item.id === value.actionFieldId);
        if (field) {
            if (seenFields.has(field.id)) return [];
            seenFields.add(field.id);
        }

        return [restoreSavedFieldValue(field ? { ...value, position: 0 } : value, field)];
    });

    for (const field of fields) {
        const alreadyHasValue = values.some((value) => value.actionFieldId === field.id);

        if (field.hidden || alreadyHasValue) {
            continue;
        }

        values.push(defaultFieldValue(field));
    }

    return values;
};

const isValidFieldValue = (field: TemplateActionField, entry: TemplateFieldValue): boolean => {
    if (entry.mode === "share") {
        if (
            !templateShareBases(field).some((base) => base?.id === entry.baseSsqPropertyId) ||
            templateShareValueError(entry) ||
            templateShareUnitMismatch(field, entry)
        ) {
            return false;
        }
    }

    if (entry.boundsEnabled && !templateAllowsBounds(field, entry)) {
        return false;
    }

    if (templateBoundsError(entry) !== null) {
        return false;
    }

    const type = templateFieldType(field);

    if (["select", "radio", "checkbox"].includes(type)) {
        const codes = entry.dataListOptionCodes ?? [];

        if (codes.length === 0) {
            return false;
        }

        return codes.every((code) => field.dataList?.options.some((option) => option.code === code));
    }

    if (entry.value.trim() === "") {
        return false;
    }

    if (type === "number") {
        return isValidStandardNumber(entry.value);
    }

    return true;
};

export const validTemplateFields = (fields: TemplateActionField[], values: TemplateFieldValue[]): boolean => {
    for (const field of fields) {
        if (field.hidden) {
            continue;
        }

        const entries = values.filter((value) => value.actionFieldId === field.id);

        if (
            entries.length !== 1 ||
            entries[0].position !== 0 ||
            entries.some((entry) => !isValidFieldValue(field, entry))
        ) {
            return false;
        }
    }

    return true;
};

export const replaceTemplateAction = (actions: TemplateAction[], action: TemplateAction) => {
    const found = actions.some((item) => item.actionId === action.actionId);

    return found ?
            actions.map((item) => (item.actionId === action.actionId ? { ...action, position: item.position } : item))
        :   [...actions, { ...action, position: actions.length }];
};

const renumberTemplateActions = (actions: TemplateAction[]) =>
    actions.map((action, position) => ({ ...action, position }));

export const removeTemplateAction = (actions: TemplateAction[], actionId: number) =>
    renumberTemplateActions(actions.filter((action) => action.actionId !== actionId));

export const moveTemplateAction = (actions: TemplateAction[], fromIndex: number, toIndex: number) => {
    const moved = [...actions];
    moved.splice(toIndex, 0, ...moved.splice(fromIndex, 1));

    return renumberTemplateActions(moved);
};
